<template>
    <div class="Container">
        <q-inner-loading :showing="isLoading" />
        <div class="TopContainer">
            <div class="ChartTitle">
                <strong style="color:#33699a;">{{ $t('booster.prediction.predictedPerformance.risk') }}</strong>
            </div>
            <QSpace />
            <QBtnToggle
                v-model="optionSel.value"
                toggleColor="primary"
                style="height: fit-content;"
                noCaps
                dense
                :options="options"
                @update:model-value="checkOptionSel"
            />
        </div>
        <div
            v-if="error"
            class="Error"
        >
            {{ error }}
        </div>
        <div
            v-else
            class="ChartContainer"
        >
            <v-chart
                v-if="!isLoading"
                ref="riskOptions"
                :option="riskOptions"
                class="Chart"
            />
        </div>
    </div>
</template>
<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest, notifyError,
} from '@/api'
import echartsColors from '@/charts/echartsColors'

export default {
    name: 'RiskEstimation',
    inject: ['App'],
    props: {
        attribute: VueTypes.string,
        processId: VueTypes.string,
    },
    data() {
        return {
            optionSel: {
                value: 'SLACOMPLIANCEFREQ',
                label: 'abs',
            },
            options: [
                {
                    value: 'SLACOMPLIANCEFREQ',
                    label: 'abs',
                },
                {
                    value: 'SLACOMPLIANCEPERCENTAGE',
                    label: '%',
                },
            ],
            slaMonths: 3,
            months: [3, 6, 9, 12],
            isLoading: false,
            response: undefined,
            error: undefined,
            riskOptions: {
                tooltip: {
                    trigger: 'axis',
                    confine: true,
                    formatter: (params) => {
                        let tooltipContent = '<div>'
                        tooltipContent += `${(params[0].name)}<br>`
                        params.forEach((param) => {
                            const { seriesName } = param
                            const { value } = param
                            const { color } = param
                            tooltipContent += `<div style="width: 10px; height: 10px; background-color: ${color};border-radius: 50%; display: inline-block; margin-right: 3px;">
                                </div>`
                            if (this.optionSel.value === 'SLACOMPLIANCEFREQ') {
                                tooltipContent += `${seriesName}: ${this.App.numberLocationFormat(value, true)} ${this.$t('booster.state.cases')}`
                            } else {
                                tooltipContent += `${seriesName}: ${this.App.numberLocationFormat(parseFloat((value).toFixed(2)), true)} %`
                            }
                            tooltipContent += '<br></div>'
                        })
                        return tooltipContent
                    },
                },
                dataZoom: [
                    {
                        type: 'slider',
                        maxValueSpan: 30,
                        minValueSpan: 10,
                        showDetail: false,
                        showDataShadow: false,
                        bottom: 10,
                        height: 15,
                    }],
                grid: {
                    left: 25,
                    right: 25,
                    bottom: 30,
                    top: 15,
                },
                xAxis: {
                    type: 'category',
                    axisPointer: {
                        type: 'shadow',
                    },
                    data: [],
                    axisTick: { show: false },
                    axisLine: {
                        show: false,
                    },
                    axisLabel: {
                        interval: 0,
                        // formatter (value) {
                        //     const maxLength = 7
                        //     if (value.length > maxLength) {
                        //         return `${value.substring(0, maxLength)}...`
                        //     // eslint-disable-next-line no-else-return
                        //     } else {
                        //         return value
                        //     }
                        // },
                        show: false,
                    },
                },
                yAxis: {
                    show: false,
                },
                series: [
                    {
                        name: this.$t('booster.state.below'),
                        type: 'bar',
                        stack: 'total',
                        label: {
                            show: false,
                            formatter: {},
                        },
                        itemStyle: {
                            borderColor: echartsColors.semantic.sla.good.border,
                            color: echartsColors.semantic.sla.good.fill,
                            borderRadius: [3, 3, 3, 3],
                        },
                        data: [],
                    },
                    {
                        name: this.$t('booster.state.intermediate'),
                        type: 'bar',
                        stack: 'total',
                        label: {
                            show: false,
                        },
                        itemStyle: {
                            borderColor: echartsColors.semantic.sla.warn.border,
                            color: echartsColors.semantic.sla.warn.fill,
                            borderRadius: [3, 3, 3, 3],
                        },
                        data: [],
                    },
                    {
                        name: this.$t('booster.state.above'),
                        type: 'bar',
                        stack: 'total',
                        label: {
                            show: false,
                        },
                        itemStyle: {
                            borderColor: echartsColors.semantic.sla.bad.border,
                            color: echartsColors.semantic.sla.bad.fill,
                            borderRadius: [3, 3, 3, 3],
                        },
                        data: [],
                    },
                ],
            },
        }
    },
    computed: {
    },
    watch: {
        attribute(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.getData()
            }
        },
    },
    mounted() {
        if (this.attribute) {
            this.getData()
            window.addEventListener('resize', this.handleResize)
        }
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize)
    },
    methods: {
        handleResize() {
            if (this.$refs.riskOptions) {
                setTimeout(() => {
                    this.$refs.riskOptions.resize()
                }, 300)
            }
        },
        getData() {
            const params = { attribute: this.attribute }
            this.resetOptions()
            this.isLoading = true
            apiRequest(Api().booster.predictedPerformanceRisk({ processId: this.processId, params }))
                .then((res) => {
                    this.response = res
                    this.checkOptionSel()
                })
                .catch((e) => {
                    if (e.result.response.data.errorCode === ('DE0076')) {
                        this.error = e.result.response.data.errorMessage
                    } else {
                        notifyError()
                    }
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        resetOptions() {
            this.response = undefined
            this.error = undefined
            this.riskOptions.xAxis.data = undefined
            this.riskOptions.series[0].data = undefined
            this.riskOptions.series[1].data = undefined
            this.riskOptions.series[2].data = undefined
        },
        checkOptionSel() {
            if (this.response) {
                this.riskOptions.xAxis.data = this.response.rows.map((i) => i.value)
                if (this.optionSel.value === 'SLACOMPLIANCEFREQ') {
                    this.riskOptions.yAxis.max = undefined
                    this.riskOptions.series[0].data = this.response.rows.map((i) => i.meetSlaAbsolute)
                    this.riskOptions.series[1].data = this.response.rows.map((i) => i.riskSlaAbsolute)
                    this.riskOptions.series[2].data = this.response.rows.map((i) => i.exceedSlaAbsolute)
                } else {
                    this.riskOptions.yAxis.max = 100
                    this.riskOptions.series[0].data = this.response.rows.map((i) => i.meetSlaPercentage * 100)
                    this.riskOptions.series[1].data = this.response.rows.map((i) => i.riskSlaPercentage * 100)
                    this.riskOptions.series[2].data = this.response.rows.map((i) => i.exceedSlaPercentage * 100)
                }
            }
        },
    },
}
</script>
<style scoped lang="scss">
.Container {
    position: relative;
    display:flex;
    height: 25vh;
    flex-direction:column;
    // flex-wrap: nowrap;
}
.TopContainer {
    display: flex;
    flex-direction: row;
    height: fit-content;
}
.ChartTitle {
    display:flex;
    justify-content: center;
    align-items: center;
}
.Error {
    padding: 1vh;
    flex-grow: 1;
  }
.ChartContainer {
    width: 100%;
    height: 25vh;
    margin:0 auto;
    white-space: nowrap;
    .Chart {
        width: 100%;
        height: 25vh;
        & + .chart {
          margin-left: 30px;
          @media screen and (max-width: $lg) {
               margin-left: 0px;
          }
        }
  }
}
.isLoading {
    display: flex;
    top: 35%;
    left: 75%;
    max-width: 50px;
    max-height: 50px;
}
</style>
